import {
  createResource,
  MatchParams,
  Query,
  RouteResource,
} from 'react-resource-router';

import { createHttpRequest } from 'src/utils/http-request';
import { HttpError } from 'src/utils/http-request-error';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { PACKAGES_NOT_FOUND_ERROR } from '../errors';
import { PackagesContext } from '../types';
import { getPackagesContext } from '../utils';

const DEFAULT_PACKAGES_RESOURCE_EXPIRY = 60000; // 60 seconds

/**
 * Extract the specified URL path params from the router context. An error will
 * be thrown if any of the specified parameters are missing from the URL.
 * @param params MatchParams object from the router
 * @param paramNames an array of path param names to extract
 * @returns a map of path param names to their values. Values are URL-decoded.
 */
const extractPathParams = (
  params: MatchParams,
  paramNames: readonly string[]
): Record<string, string> =>
  paramNames.reduce((acc, paramName) => {
    const value = params[paramName];
    if (!value) {
      throw new Error(`Route is missing ${paramName} parameter in path`);
    }
    acc[paramName] = decodeURIComponent(value);

    return acc;
  }, {} as Record<string, string>);

/**
 * Extract the specified query params from the router context. Query params without
 * a value will not be included in the returned Record.
 * @param params Query object from the router
 * @param paramNames an array of query param names to extract
 * @returns a map of path param names to their values. Values are URL-decoded.
 */
const extractQueryParams = (
  params: Query,
  paramNames: readonly string[]
): Record<string, string | undefined> =>
  paramNames.reduce((acc, paramName) => {
    const value = params[paramName];
    if (value) {
      acc[paramName] = decodeURIComponent(value);
    }

    return acc;
  }, {} as Record<string, string | undefined>);

type PackagesResourceFactoryParams<
  PathParamNames extends readonly string[],
  QueryParamNames extends readonly string[],
  ResponseType,
  TransformedResponseType
> = {
  key: string;
  pathParamNames: PathParamNames;
  queryParamNames: QueryParamNames;
  getRequestUrl: (
    context: PackagesContext,
    pathParams: Record<PathParamNames[number], string>,
    queryParams: Record<QueryParamNames[number], string | undefined>
  ) => string;
  maxAge?: number;
  transformResponse?: (response: ResponseType) => TransformedResponseType;
};

/**
 * Factory method for generating route resources that fetch data from the
 * bbc-packages service. Supports fetching from different URLs depending on the
 * page context (workspace/project/repository), and transforming the response
 * from the API.
 *
 * To use, first call the factory method passing in a type parameter
 * representing the shape of the response returned by the API. Then call the
 * returned value passing in the PackagesResourceFactoryParams object.
 *
 * The PackagesResourceFactoryParams are:
 *
 * - `key` - a unique key for this resource
 *
 * - `pathParamNames` - a readonly array of expected path parameter names,
 * excluding `workspaceSlug`, `projectKey`, `repositoryOwner`, `repositorySlug`.
 * When passing this in, make sure to type assert with `as const` for better
 * type inference.
 *
 * - `queryParamNames` - a readonly array of expected query parameter names.
 * When passing this in, make sure to type assert with `as const` for better
 * type inference.
 *
 * - `getRequestUrl` - a function that accepts a PackagesContext object and the
 * parsed path/query parameter objects. This function should return the URL to
 * fetch data from as a string, including all query parameters. Note that the
 * passed path/query parameter values are already URL-decoded.
 *
 * - `maxAge` - an optional parameter representing the max age of this resource
 * in milliseconds
 *
 * - `transformResponse` - an optional function for transforming the API
 * response
 */

export const packagesResourceFactory =
  <ResponseType>() =>
  <
    PathParams extends readonly string[],
    QueryParams extends readonly string[],
    TransformedResponseType = ResponseType
  >({
    key,
    pathParamNames,
    queryParamNames,
    getRequestUrl,
    maxAge = DEFAULT_PACKAGES_RESOURCE_EXPIRY,
    transformResponse,
  }: PackagesResourceFactoryParams<
    PathParams,
    QueryParams,
    ResponseType,
    TransformedResponseType
  >): RouteResource<TransformedResponseType> =>
    createResource<TransformedResponseType>({
      type: key,
      maxAge,
      getKey: ({ match: { params }, query }) => {
        const context = getPackagesContext(params);
        const pathParams = extractPathParams(params, pathParamNames);
        const queryParams = extractQueryParams(query, queryParamNames);
        return [
          key,
          ...Object.values(context),
          ...Object.values(pathParams),
          ...Object.values(queryParams).filter(Boolean),
        ].join('/');
      },
      getData: async ({ match: { params }, query }) => {
        const context = getPackagesContext(params);
        const pathParams = extractPathParams(params, pathParamNames);
        const queryParams = extractQueryParams(query, queryParamNames);
        const url = getRequestUrl(context, pathParams, queryParams);

        try {
          const response = await createHttpRequest<ResponseType>('GET', {
            throwErrorType: 'errorObject',
          })(url);

          if (transformResponse) {
            return transformResponse(response);
          } else {
            return response as TransformedResponseType;
          }
        } catch (error) {
          if ((error as HttpError)?.status === 404) {
            // Special error type that can be used to display a 'Not found' view
            throw PACKAGES_NOT_FOUND_ERROR;
          }

          captureExceptionWithTags(error);
          throw error;
        }
      },
    });
